import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AccountSecurityOverview from '../components/AccountSecurityOverview';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

const AccountStatusPage = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ページタイトル設定
  useEffect(() => {
    document.title = 'アカウント状態 | つむぎ';
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchUserProfile();
  }, [isAuthenticated, navigate]);

  const fetchUserProfile = async () => {
    try {
      const profileResponse = await userService.getProfile();
      setUserProfile(profileResponse.user);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUserProfile();
    // Force refresh user data from auth context
    if (updateUser) {
      try {
        const response = await authService.getProfile();
        updateUser(response.user);
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    }
    setRefreshing(false);
  };

  const handleBackToDashboard = () => {
    navigate('/mypage');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">アカウント情報を読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">データの読み込みに失敗しました</h2>
          <p className="text-gray-600 mb-4">アカウント情報を取得できませんでした。</p>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToDashboard}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                ダッシュボードに戻る
              </button>
              <div className="h-6 border-l border-gray-300" />
              <h1 className="text-2xl font-bold text-gray-900">アカウントセキュリティ</h1>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{refreshing ? '更新中...' : '更新'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* 概要説明 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h2 className="font-medium text-blue-800 mb-1">アカウントセキュリティについて</h2>
                <p className="text-sm text-blue-700">
                  このページでは、あなたのアカウントのセキュリティ状態を確認できます。
                  メール認証の完了は、アカウントの安全性を大幅に向上させる重要なステップです。
                </p>
              </div>
            </div>
          </div>

          {/* メインセキュリティ表示 */}
          <AccountSecurityOverview 
            user={userProfile} 
            onProfileUpdate={handleRefresh}
          />

          {/* アクション推奨セクション */}
          {!userProfile.isEmailVerified && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <svg className="h-5 w-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                </svg>
                <span>推奨アクション</span>
              </h3>
              
              <div className="space-y-4">
                <div className="border-l-4 border-orange-400 bg-orange-50 p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <span className="text-2xl">📧</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-orange-800 mb-2">メールアドレスの認証</h4>
                      <p className="text-sm text-orange-700 mb-3">
                        メール認証を完了することで、以下のメリットがあります：
                      </p>
                      <ul className="text-sm text-orange-700 space-y-1 mb-4">
                        <li className="flex items-center space-x-2">
                          <span className="text-green-600">✓</span>
                          <span>アカウント乗っ取りからの保護</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-600">✓</span>
                          <span>パスワードリセット機能の利用</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-600">✓</span>
                          <span>重要な通知の受信</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-600">✓</span>
                          <span>有料プラン機能の完全利用</span>
                        </li>
                      </ul>
                      
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <p className="text-sm text-orange-800 mb-2">
                          <strong>📌 次のステップ:</strong>
                        </p>
                        <ol className="text-sm text-orange-700 space-y-1">
                          <li>1. 上記の「認証メール送信」ボタンをクリック</li>
                          <li>2. {userProfile.email} をチェック</li>
                          <li>3. メール内の認証リンクをクリック</li>
                          <li>4. このページに戻って完了確認</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* セキュリティベストプラクティス */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>セキュリティのベストプラクティス</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">🔐 強固なパスワード</h4>
                <p className="text-sm text-gray-600">
                  大小文字、数字、記号を含む12文字以上のパスワードを使用しましょう。
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">📱 定期的なログイン</h4>
                <p className="text-sm text-gray-600">
                  アカウントを定期的に使用することで、不正アクセスを早期発見できます。
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">🔄 情報の更新</h4>
                <p className="text-sm text-gray-600">
                  メールアドレスや個人情報は常に最新の状態に保ちましょう。
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">⚠️ 怪しい活動</h4>
                <p className="text-sm text-gray-600">
                  身に覚えのない活動があればすぐにパスワードを変更してください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountStatusPage;